/* eslint-disable prefer-template */
import { Logger, isEmpty, I18n, Hub } from '@aws-amplify/core';
import { Auth } from '@aws-amplify/auth';
import {
  AuthState,
  AuthStateHandler,
  ChallengeName,
  CognitoUserInterface,
  TOAST_AUTH_ERROR_EVENT,
  UI_AUTH_CHANNEL,
} from '@aws-amplify/ui-components';

const logger = new Logger('auth-helpers');

async function checkContact(user: CognitoUserInterface, handleAuthStateChange: AuthStateHandler) {
  try {
    const data = await Auth.verifiedContact(user);
    if (!isEmpty(data.verified) || isEmpty(data.unverified)) {
      handleAuthStateChange(AuthState.SignedIn, user);
    } else {
      const newUser = Object.assign(user, data);
      handleAuthStateChange(AuthState.VerifyContact, newUser);
    }
  } catch (err) {
    const error = err as Error;
    Hub.dispatch(UI_AUTH_CHANNEL, {
      event: TOAST_AUTH_ERROR_EVENT,
      message: I18n.get(error.message) ?? error.message,
    });
  }
}

export const handleSignIn = async (
  username: string,
  password: string,
  handleAuthStateChange: AuthStateHandler,
) => {
  try {
    const user = await Auth.signIn(username, password);
    logger.debug(user);
    if (
      user.challengeName === ChallengeName.SMSMFA ||
      user.challengeName === ChallengeName.SoftwareTokenMFA
    ) {
      logger.debug('confirm user with ' + user.challengeName);
      handleAuthStateChange(AuthState.ConfirmSignIn, user);
    } else if (user.challengeName === ChallengeName.NewPasswordRequired) {
      logger.debug('require new password', user.challengeParam);
      handleAuthStateChange(AuthState.ResetPassword, user);
    } else if (user.challengeName === ChallengeName.MFASetup) {
      logger.debug('TOTP setup', user.challengeParam);
      handleAuthStateChange(AuthState.TOTPSetup, user);
    } else if (
      user.challengeName === ChallengeName.CustomChallenge &&
      user.challengeParam &&
      user.challengeParam.trigger === 'true'
    ) {
      logger.debug('custom challenge', user.challengeParam);
      handleAuthStateChange(AuthState.CustomConfirmSignIn, user);
    } else {
      await checkContact(user, handleAuthStateChange);
    }
  } catch (err) {
    const error = err as Error;
    Hub.dispatch(UI_AUTH_CHANNEL, {
      event: TOAST_AUTH_ERROR_EVENT,
      message: I18n.get(error.message) ?? error.message,
    });
    if (error.name === 'UserNotConfirmedException') {
      logger.debug('the user is not confirmed');
      handleAuthStateChange(AuthState.ConfirmSignUp, { username });
    } else if (error.name === 'PasswordResetRequiredException') {
      logger.debug('the user requires a new password');
      handleAuthStateChange(AuthState.ForgotPassword, { username });
    }
  }
};
